<template>
  <div class="service-wrap pt-20 pb-20">
    <div class="container">

      <!-- Liste des articles -->
      <div class="row">
        <div 
          class="col-xl-3 col-lg-4 col-md-6" 
          v-for="(article, index) in articles" 
          :key="index"
        >
          <div class="team-card style1">
            <div 
              class="team-img"
              :style="{
                height: '250px',
                backgroundImage: `url(${getUrlApiForFiles(article.image)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderRadius: '10px',
                cursor: 'pointer'
              }"
              @click="openModal(article)"
            >
              <div class="team-overlay">
                <i class="flaticon-eye"></i>
              </div>
            </div>
            <div class="team-info text-center mt-3">
              <h4 class="truncate-text">{{ article.titre }}</h4>
              <span class="team-designation">
                {{ formatDate(article.datePublication) }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-4">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>

    <!-- Modal : aperçu de l’article -->
    <div v-if="showModal" class="photo-modal" @click="closeModal">
      <div class="modal-content" @click.stop>
        <span class="close-btn" @click="closeModal">&times;</span>
        <img :src="getUrlApiForFiles(selectedArticle.image)" :alt="selectedArticle.titre" />
        <div class="modal-info">
          <h3>{{ selectedArticle.titre }}</h3>
          <p class="text-muted">{{ formatDate(selectedArticle.datePublication) }}</p>
          <p class="mt-3">{{ selectedArticle.contenu }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ApiService from "@/services/ApiService";
import { getUrlApiForFiles, error } from "@/utils/utils";
import { defineComponent, ref, onMounted } from "vue";
import PaginationComponent from '@/components/Utilities/Pagination.vue';

interface Article {
  id: number;
  titre: string;
  contenu: string;
  image: string;
  datePublication: string;
}

export default defineComponent({
  name: 'ArtiCle',
  components: { PaginationComponent },
  setup() {
    const articles = ref<Array<Article>>([]);
    const isLoading = ref(false);
    const showModal = ref(false);
    const selectedArticle = ref<Article | null>(null);

    // PAGINATION
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(12);
    const totalElements = ref(0);

    const handlePaginate = ({ page_, limit_ }) => {
      page.value = page_;
      getArticles(page_, limit_);
    };

    //  Récupération des articles
    function getArticles(pageNum = 1, limi = 12) {
      isLoading.value = true;
      return ApiService.get(`/articles?page=${pageNum}&limit=${limi}`)
        .then(({ data }) => {
          articles.value = data.data.data;
          totalPages.value = data.data.totalPages;
          limit.value = data.data.limit;
          totalElements.value = data.data.totalElements;
        })
        .catch(({ response }) => {
          error(response?.data?.message || "Erreur de chargement des articles");
          articles.value = [];
        })
        .finally(() => {
          isLoading.value = false;
        });
    }

    function openModal(article: Article) {
      selectedArticle.value = article;
      showModal.value = true;
    }

    function closeModal() {
      showModal.value = false;
    }

    // Formatage de la date (jj/mm/aaaa)
    function formatDate(dateStr: string) {
      const date = new Date(dateStr);
      return date.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "long",
        day: "numeric"
      });
    }

    onMounted(() => {
      getArticles();
    });

    return {
      articles,
      isLoading,
      showModal,
      selectedArticle,
      getUrlApiForFiles,
      formatDate,
      page,
      totalPages,
      limit,
      totalElements,
      handlePaginate,
      openModal,
      closeModal
    };
  },
});
</script>

<style scoped>
.team-card {
  transition: transform 0.3s ease;
}

.team-card:hover {
  transform: translateY(-5px);
}

.team-img {
  position: relative;
  overflow: hidden;
}

.team-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 10px;
}

.team-img:hover .team-overlay {
  opacity: 1;
}

.team-overlay i {
  font-size: 32px;
  color: white;
}

.team-info h4 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #1a1a1a;
}

.team-designation {
  font-size: 14px;
  color: #666;
}

/* Troncature pour le titre */
.truncate-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Modal */
.photo-modal {
  position: fixed;
  z-index: 9999;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  position: relative;
  max-width: 800px;
  width: 90%;
  background: white;
  border-radius: 10px;
  overflow: hidden;
}

.modal-content img {
  width: 100%;
  height: auto;
  display: block;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 25px;
  color: white;
  font-size: 40px;
  font-weight: bold;
  cursor: pointer;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.5);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.8);
}

.modal-info {
  padding: 20px;
  text-align: center;
}

.modal-info h3 {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 10px;
}

.modal-info p {
  font-size: 16px;
  color: #444;
}
</style>
